import { ChatAgent } from '../@types/EnumsAI.enum';
import { ToolVertex } from '../@types/IA.type';
import HERU_API from '../shared/constants/heruApi';
import STORAGE_KEYS from '../shared/constants/storage';
import { get, post } from '../shared/utils/FetchSSL.util';
import { useStorage } from '../shared/utils/general.util';

const getOnboardingWelcome = async (message: string, tokenSession?: string) => {
	try {
		const body = {
			message,
		};
		const response = await post(HERU_API.ONBOARDING_WELCOME, body, {
			token: true,
			isNewGateway: true,
			tokenSession,
		});
		return response;
	} catch (error) {
		throw error;
	}
};

type OnboardingRecommendationBody = {
	recommendation: string;
	userContext: string;
};

const getOnboardingRecommendation = async (
	body: OnboardingRecommendationBody,
	tokenSession?: string,
) => {
	try {
		const response = await post(HERU_API.ONBOARDING_RECOMMENDATION, body, {
			token: true,
			isNewGateway: true,
			tokenSession,
		});
		return response;
	} catch (error) {
		throw error;
	}
};

const getOnboardingIA = async (userId: number, tokenSession?: string) => {
	try {
		const response = await get(
			HERU_API.HERU_API_IA_SAMANTHA_ONBOARDING(userId),
			{
				token: true,
				isNewGateway: true,
				tokenSession,
				directURL:
					import.meta.env.VITE_AI_ENV === 'DEV'
						? `${import.meta.env.VITE_GCP_API_URL}/api/v1/heru-ai-service/samantha/onboarding/${userId}`
						: undefined,
			},
		); 
		const analysisNotFound = response?.statusCode === 400;
		const percentage = response?.percentage?.percentage;
		if (
			percentage === undefined ||
			percentage === null || 
			analysisNotFound
		) {
			throw new Error('No se pudo obtener el progreso del onboarding');
		}
		return response;
	} catch (error: any) {
		useStorage().setItem(
			STORAGE_KEYS.ONBOARDING_FAILED_DATE,
			JSON.stringify(new Date()),
		);
		throw new Error('No se pudo obtener el progreso del onboarding');
	}
};

type ChatIASamanthaBody = {
	userMessage: string;
	userName: string;
	assistantLatestQuestion: string;
	userId: number;
};

const postChatIASamantha = async (
	body: ChatIASamanthaBody,
	tokenSession?: string,
) => {
	try {
		const response = await post(HERU_API.HERU_API_IA_SAMANTHA_CHAT, body, {
			token: true,
			isNewGateway: true,
			tokenSession,
			directURL:
				import.meta.env.VITE_AI_ENV === 'DEV'
					? `${import.meta.env.VITE_GCP_API_URL}/api/v1/heru-ai-service/samantha/chat`
					: undefined,
		});

		if (!response?.assistantResponse) {
			throw new Error('No se pudo obtener la respuesta del asistente');
		}
		return response;
	} catch (error) {
		useStorage().setItem(
			STORAGE_KEYS.ONBOARDING_FAILED_DATE,
			JSON.stringify(new Date()),
		);
		throw error;
	}
};

const getOnboardingAnalysisIA = async (
	userId: number,
	onboardingId: number,
	tokenSession?: string,
) => {
	try {
		const response = await get(
			HERU_API.HERU_API_IA_SAMANTHA_ANALYSIS(userId, onboardingId),
			{
				token: true,
				isNewGateway: true,
				tokenSession,
				directURL:
					import.meta.env.VITE_AI_ENV === 'DEV'
						? `${import.meta.env.VITE_GCP_API_URL}/api/v1/heru-ai-service/samantha/onboarding-analizer/${userId}/${onboardingId}`
						: undefined,
			},
		);

		return response;
	} catch (error) {
		useStorage().setItem(
			STORAGE_KEYS.ONBOARDING_FAILED_DATE,
			JSON.stringify(new Date()),
		);
		throw error;
	}
};

// ========= WEB CHAT AI =========
const getChatsAI = async (userId: number, tokenSession?: string) => {
	try {
		const response = await get(`${HERU_API.WEB_CHAT_AI}`, {
			token: true,
			isNewGateway: true,
			tokenSession,
			directURL:
				import.meta.env.VITE_AI_ENV === 'DEV'
					? `${import.meta.env.VITE_GCP_API_URL}/api/v1/heru-ai-service/web-chat-ai/chats/${userId}`
					: undefined,
		});

		return response;
	} catch (error) {
		throw error;
	}
};

const getMessageRecommendations = async (
	agentName: ChatAgent,
	tokenSession?: string,
) => {
	try {
		const response = await get(
			`/ai/chat/message-recommendations/${agentName}`,
			{
				token: true,
				isNewGateway: true,
				tokenSession,
				directURL:
					import.meta.env.VITE_AI_ENV === 'DEV'
						? `${import.meta.env.VITE_GCP_API_URL}/api/v1/heru-ai-service/agents/message-recommendations/${agentName}`
						: undefined,
			},
		);

		return response;
	} catch (error) {
		throw error;
	}
};

const chatAgentAI = async ({
	userId,
	thread,
	tokenSession,
	additionalInfo,
	tools,
	threadId,
	agentName,
}: {
	userId: number;
	agentName: ChatAgent;
	thread: Array<{
		role: string;
		content: string;
		uuid?: string;
		timestamp?: string;
		agent?: string;
		tools?: any[];
	}>;
	tokenSession?: string;
	additionalInfo?: string;
	tools?: ToolVertex[];
	threadId?: number;
}) => {
	try {
		const response = await post(
			`/ai/chat`,
			{
				thread,
				threadId,
				agentName,
				additionalInfo,
				tools,
			},
			{
				token: true,
				isNewGateway: true,
				tokenSession,
				directURL:
					import.meta.env.VITE_AI_ENV === 'DEV'
						? `${import.meta.env.VITE_GCP_API_URL}/api/v1/heru-ai-service/agents/chat/${userId}`
						: undefined,
			},
		);

		return response;
	} catch (error) {
		throw error;
	}
};

const updateMessageChatAI = async ({
	threadId,
	messageUUID,
	field,
	value,
	tokenSession,
}: {
	threadId: number;
	messageUUID: string;
	field: string;
	value: string[];
	tokenSession?: string;
}) => {
	try {
		const response = await post(
			`/ai/chat/update-message`,
			{
				threadId,
				messageUUID,
				field,
				value,
			},
			{
				token: true,
				isNewGateway: true,
				tokenSession,
				directURL:
					import.meta.env.VITE_AI_ENV === 'DEV'
						? `${import.meta.env.VITE_GCP_API_URL}/api/v1/heru-ai-service/web-chat-ai/update-thread-message`
						: '',
			},
		);

		return response;
	} catch (error) {
		throw error;
	}
};

// FORMULARIO DE ESCALACIÓN
const sendEscalationForm = async (body: any, tokenSession?: string) => {
	try {
		const response = await post('/ai/escalation', body, {
			token: true,
			isNewGateway: true,
			tokenSession,
		});

		return response;
	} catch (error) {
		throw error;
	}
};
export {
	getOnboardingWelcome,
	getOnboardingRecommendation,
	postChatIASamantha,
	getOnboardingIA,
	getOnboardingAnalysisIA,
	getChatsAI,
	chatAgentAI,
	updateMessageChatAI,
	sendEscalationForm,
	getMessageRecommendations,
};
